import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Package, Star, Filter } from 'lucide-react';
import Layout from '../Layout';
import { API_BASE_URL, getData } from '../../helpers/ApiSettings';
import { SaleCartManager } from '../../helpers/SaleCartManager';
import { showToast } from '../../helpers/customToast';
import { checkIsReferenced } from '../../helpers/user';
import { useDeliveryThreshold } from '../../helpers/useDeliveryThreshold';

const ProductSaleListingPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cartCount, setCartCount] = useState(0);
  const [isReferenced, setIsReferenced] = useState(false);
  const freeDeliveryThreshold = useDeliveryThreshold();

  useEffect(() => {
    (async () => {
      const referenced = await checkIsReferenced();
      setIsReferenced(referenced);
      fetchProducts();
      fetchCategories();
      updateCartCount();
    })();
  }, []);

  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selectedCategory, isReferenced]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await getData('/product-sales?populate[images]=*&populate[sale_category]=*&filters[is_active][$eq]=true&sort=featured:desc,createdAt:desc');
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      showToast('Erreur lors du chargement des produits', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getData('/sale-categories?sort[0]=order:asc&sort[1]=name:asc');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sale categories:', error);
    }
  };

  const filterProducts = () => {
    // Tous les produits sont visibles à tous. Le statut "vente privée" sert
    // uniquement à afficher un tarif réduit aux clients référencés (cf.
    // renderProductCard ci-dessous).
    if (selectedCategory === 'all') {
      setFilteredProducts(products);
    } else {
      const categoryId = parseInt(selectedCategory, 10);
      setFilteredProducts(products.filter(product => product.sale_category?.id === categoryId));
    }
  };

  const updateCartCount = () => {
    setCartCount(SaleCartManager.getItemCount());
  };

  const handleAddToCart = (product, isBulk = false) => {
    const success = SaleCartManager.addToCart(product, 1, isBulk);
    if (success) {
      showToast(
        `${product.title} ajouté au panier ${isBulk ? '(lot)' : '(unité)'}`, 
        'success'
      );
      updateCartCount();
    } else {
      showToast('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  // Helper function to truncate HTML content
  const truncateHtmlContent = (htmlString, maxLength = 100) => {
    if (!htmlString) return '';
    
    // Create a temporary div to strip HTML tags for length calculation
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (textContent.length <= maxLength) {
      return htmlString;
    }
    
    // If we need to truncate, do it on the text content and add ellipsis
    const truncatedText = textContent.substring(0, maxLength) + '...';
    return truncatedText;
  };

  const renderProductCard = (product) => {
    const hasBulkOption = product.bulk_price && product.bulk_quantity > 1;
    const hasPrivateOffer =
      (product.is_private === true || product.sale_category?.is_private === true) &&
      product.private_price != null;
    // Référencé + offre privée → on lui affiche le prix privé.
    // Sinon → prix public standard.
    const showsPrivatePrice = hasPrivateOffer && isReferenced;

    return (
      <div key={product.id} className="col-lg-4 col-md-6 mb-4">
        <div className="card h-100">
          {product.featured && (
            <div className="position-absolute top-0 start-0 bg-warning text-dark px-2 py-1 small" style={{zIndex: 1}}>
              <Star size={14} className="me-1" />
              Vedette
            </div>
          )}
          {showsPrivatePrice && (
            <div className="position-absolute top-0 end-0 bg-dark text-white px-2 py-1 small" style={{zIndex: 1}}>
              Tarif vente privée
            </div>
          )}

          <Link to={`/vente/produit/${product.documentId}`}>
            <img
              src={API_BASE_URL + product.images[0]?.url}
              alt={product.title}
              className="card-img-top"
              style={{ height: '250px', objectFit: 'cover' }}
            />
          </Link>

          <div className="card-body d-flex flex-column">
            <h5 className="card-title">
              <Link to={`/vente/produit/${product.documentId}`} className="text-decoration-none">
                {product.title}
              </Link>
            </h5>

            {product.brand && (
              <p className="text-muted small mb-2">{product.brand}</p>
            )}

            <div className="card-text flex-grow-1">
              <div
                dangerouslySetInnerHTML={{
                  __html: truncateHtmlContent(product.description, 100)
                }}
              />
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Prix unitaire:</span>
                {showsPrivatePrice ? (
                  <span className="h6 mb-0">
                    <span className="text-decoration-line-through text-muted me-2 small">
                      {product.unit_price.toFixed(2)}€
                    </span>
                    <span className="text-success">{product.private_price.toFixed(2)}€</span>
                  </span>
                ) : (
                  <span className="h6 mb-0">{product.unit_price.toFixed(2)}€</span>
                )}
              </div>

              {hasBulkOption && (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">À partir de {product.bulk_quantity}:</span>
                  <span className="h6 mb-0 text-success">{product.bulk_price}€/unité</span>
                </div>
              )}
            </div>

            {/* <div className="mb-3">
              <small className="text-muted">
                <Package size={14} className="me-1" />
                Stock: {product.stock_quantity} disponible(s)
              </small>
            </div> */}

            <div className="mt-auto">
              <div className="d-grid gap-2">
                <button
                  className="btn btn-primary"
                  onClick={() => handleAddToCart(product, false)}
                >
                  <ShoppingCart size={16} className="me-2" />
                  Ajouter au panier
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container my-5">
        <div className="row mb-4">
          <div className="col-md-8">
            <h1>Boutique Produits Bébé</h1>
            <p className="text-muted">Découvrez notre sélection de produits soins et bien-être pour bébé</p>
          </div>
          <div className="col-md-4 text-end">
            <Link to="/vente/panier" className="btn btn-outline-primary position-relative">
              <ShoppingCart size={20} className="me-2" />
              Panier
              {cartCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Free delivery banner */}
        <div className="alert alert-info mb-4">
          <strong>Livraison gratuite</strong> à partir de {freeDeliveryThreshold}€ d'achat !
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <Filter size={20} className="me-2" />
                  Filtres
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="category" className="form-label">Catégorie</label>
                    <select
                      id="category"
                      className="form-select"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">Tous les produits</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products grid */}
        <div className="row">
          {filteredProducts.length === 0 ? (
            <div className="col-12">
              <div className="alert alert-info text-center">
                Aucun produit trouvé dans cette catégorie.
              </div>
            </div>
          ) : (
            filteredProducts.map(renderProductCard)
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProductSaleListingPage;